/* eslint-disable no-param-reassign */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { MdOutlineWidgets } from 'react-icons/md';
import { HiPlus } from 'react-icons/hi';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import Swal from 'sweetalert2';

import getValidationErros from '~/utils/getValidationsErrors';
import api from '~/services/api';

import { Container } from './styles';
import { ICategoryData } from '../Categories/Modality';
import Modality from './Modality';
import Loading from '~/components/Loading';
import { IBatchCategories, IBatchCategory } from '../Interfaces';
import { IBatch } from '../Batches';
import { generateArrays } from '..';

export interface IModalityData {
  input_id: number;
  id?: number;
  name?: string;
  order?: number;
  categories?: ICategoryData[];
}

interface IModalities {
  event_id: number;
  sold?: boolean;
  progressNumber: number;

  onChange(
    data: IModalityData[],
    batchesCategories: IBatchCategory[],
    batches?: IBatch[]
  ): void;
  typeSelected?: number;
  className?: string;
}

export interface IModalitiesHandle {
  submit: () => void;
}

const Modalities: ForwardRefRenderFunction<IModalitiesHandle, IModalities> = (
  { event_id, sold, onChange, typeSelected, className, progressNumber },
  ref
) => {
  const formRef = useRef<FormHandles>(null);
  const [modalities, setModalities] = useState<IModalityData[]>([
    {
      input_id: new Date().getTime(),
    },
  ]);
  const [oldModalities, setOldModalities] = useState<IModalityData[]>([]);
  const [modalitiesError, setModalitiesError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Carregando...');
  const [batchesCategories, setBatchesCategories] = useState<IBatchCategory[]>(
    []
  );
  const [batches, setBatches] = useState<IBatch[]>([]);

  useEffect(() => {
    if (progressNumber === 5) {
      api
        .get(`batches-categories/events/${event_id}`)
        .then(async (response) => {
          const responseBatches = await api.get(`batches/events/${event_id}`);

          const { modalities: modalitiesData, batches: batchesData } =
            generateArrays(response.data, responseBatches.data);

          const batchesCategoriesData = response.data.map(
            (batchCategory: IBatchCategories) => ({
              id: batchCategory.id,
              batch_id: batchCategory.batch_id,
              category_id: batchCategory.category_id,
              modality_id: batchCategory.modality_id,
              event_id: batchCategory.event_id,
              price: batchCategory.price,
              quantity: batchCategory.quantity,
              quantity_sold: batchCategory.quantity_sold,
              on: batchCategory.on,
            })
          );

          setModalities(modalitiesData);
          setOldModalities(modalitiesData);
          setBatchesCategories(batchesCategoriesData);
          setBatches(batchesData);
        });
    }
  }, [event_id, progressNumber]);

  const handleAddModality = useCallback(() => {
    setModalities((oldState) => [
      ...oldState,
      {
        input_id: new Date().getTime(),
      },
    ]);
  }, []);

  const handleRemoveModality = useCallback(
    (input_id) => {
      const newModalities = modalities.filter(
        (modality) => modality.input_id !== input_id
      );
      if (newModalities.length === 0) {
        newModalities.push({
          input_id: new Date().getTime(),
        });
      }

      setModalities(newModalities);
    },
    [modalities]
  );

  const handleChangeModality = useCallback(
    (value, modalityIndex) => {
      const newModalitiesData = modalities.slice();
      newModalitiesData[modalityIndex].name = value;
    },
    [modalities]
  );

  const handleChangeModalityOrder = useCallback(
    (result) => {
      if (!result.destination) return;
      const reordered = modalities.slice();
      const [removed] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, removed);
      setModalities(reordered);
    },
    [modalities]
  );

  const handleSubmit = useCallback(
    async (data) => {
      if (loading) return;
      try {
        setLoading(true);
        setLoadingText('Salvando...');
        formRef.current?.setErrors({});
        setModalitiesError('');

        const schema = Yup.object().shape({
          modalities: Yup.string().test(
            'checkModalities',
            'Favor preencher todas as modalidades',
            async () => {
              return modalities.every((modality) => {
                return !!modality.name;
              });
            }
          ),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const deletedModalities = oldModalities.filter(
          (oldModality) =>
            !modalities.some(
              (modality) => modality.input_id === oldModality.input_id
            )
        );

        const newModalities = JSON.parse(
          JSON.stringify(modalities)
        ) as IModalityData[];

        const newBatchesCategories = JSON.parse(
          JSON.stringify(batchesCategories)
        ) as IBatchCategory[];

        if (modalities.length > 0) {
          let modalityIndex = 0;
          for (const modality of modalities) {
            if (!modality.id) {
              const modalityFormData = {
                type_id: typeSelected,
                name: modality.name,
                order: modalityIndex + 1,
              };
              const responseModality = await api.post(
                'modalities',
                modalityFormData
              );
              modality.id = responseModality.data.id;
            } else {
              const modalityFormData = {
                type_id: typeSelected,
                name: modality.name,
                order: modalityIndex + 1,
              };
              await api.put(`modalities/${modality.id}`, modalityFormData);
            }

            const isOldModality = oldModalities.some((oldModality) => {
              return oldModality.input_id === modality.input_id;
            });

            if (!isOldModality) {
              if (batches && batches.length > 0) {
                for (const batch of batches) {
                  const batchCategoryFormData = {
                    modality_id: modality.id,
                    batch_id: batch.input_id,
                    event_id,
                  };

                  const responseBatchCategory = await api.post(
                    'batches-categories',
                    batchCategoryFormData
                  );

                  newBatchesCategories.push({
                    id: responseBatchCategory.data.id,
                    event_id,
                    modality_id: Number(modality.id),
                    batch_id: batch.input_id,
                    price: '0',
                    quantity: 0,
                    quantity_sold: 0,
                    new: true,
                  });

                  newModalities[modalityIndex].input_id =
                    responseBatchCategory.data.id;
                  newModalities[modalityIndex].id = modality.id;
                }
              } else {
                const batchCategoryFormData = {
                  modality_id: modality.id,
                  event_id,
                };

                const responseBatchCategory = await api.post(
                  'batches-categories',
                  batchCategoryFormData
                );
                newBatchesCategories.push({
                  id: responseBatchCategory.data.id,
                  event_id,
                  modality_id: Number(modality.id),
                  price: '0',
                  quantity: 0,
                  quantity_sold: 0,
                  new: true,
                });

                newModalities[modalityIndex].input_id =
                  responseBatchCategory.data.id;
                newModalities[modalityIndex].id = modality.id;
              }
            } else {
              const batchCategoryFormData = {
                modality_id: modality.id,
                event_id,
              };

              const responseBatchCategory = await api.put(
                `batches-categories/${modality.input_id}`,
                batchCategoryFormData
              );
              const batchCategoryIndex = newBatchesCategories.findIndex(
                (batchCategory) =>
                  batchCategory.id === responseBatchCategory.data.id
              );
              if (batchCategoryIndex >= 0) {
                newBatchesCategories[batchCategoryIndex].modality_id = Number(
                  modality.id
                );
              }
            }

            modalityIndex += 1;
          }
        }

        let newBatches: IBatch[] | undefined;

        // Só processa batches se existir
        if (batches && batches.length > 0) {
          newBatches = JSON.parse(JSON.stringify(batches)) as IBatch[];

          // Converte as datas de volta para Date objects
          newBatches.forEach((batch) => {
            if (batch.expiration) {
              batch.expiration = new Date(batch.expiration);
            }
          });
        }

        if (deletedModalities.length > 0) {
          for (const deletedModality of deletedModalities) {
            if (batches && batches.length > 0) {
              let batchIndex = 0;
              for (const batch of batches) {
                const deletedBatchesCategories =
                  batch.batchesCategories?.filter(
                    (batchCategory) =>
                      batchCategory.modality_id === deletedModality.id
                  );
                for (const deletedBatchesCategory of deletedBatchesCategories ||
                  []) {
                  await api.delete(
                    `batches-categories/${deletedBatchesCategory.id}`
                  );

                  if (newBatches) {
                    const batchCategoryIndex = newBatches[
                      batchIndex
                    ].batchesCategories?.findIndex(
                      (batchCategory) =>
                        batchCategory.id === deletedBatchesCategory.id
                    );

                    if (
                      typeof batchCategoryIndex !== 'undefined' &&
                      batchCategoryIndex >= 0
                    ) {
                      newBatches[batchIndex].batchesCategories?.splice(
                        batchCategoryIndex,
                        1
                      );
                    }
                  }
                }

                batchIndex += 1;
              }
            }

            if (
              deletedModality.categories &&
              deletedModality.categories.length > 0
            ) {
              for (const category of deletedModality.categories) {
                await api.delete(`categories/${category.id}`);
              }
            }

            await api.delete(`modalities/${deletedModality.id}`);
            // await api.delete(`batches-categories/${deletedModality.input_id}`);
          }
        }
        // setTimeout(() => {
        try {
          await api.delete(`batches-categories/events/${event_id}`);
        } catch (error) {
          console.log(error);
        }
        // }, 1000);

        setModalities(newModalities);
        setOldModalities(JSON.parse(JSON.stringify(newModalities)));
        onChange(newModalities, newBatchesCategories, newBatches);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErros(error);
          formRef.current?.setErrors(errors);
          if (errors.modalities) {
            setModalitiesError(errors.modalities);
          }
        } else {
          Swal.fire('Oops...', 'Erro ao salvar as modalidades!');
        }
      } finally {
        setLoading(false);
        setLoadingText('Carregando...');
      }
    },
    [
      batches,
      batchesCategories,
      event_id,
      loading,
      modalities,
      oldModalities,
      onChange,
      typeSelected,
    ]
  );

  useImperativeHandle(
    ref,
    () => ({
      submit: () => {
        formRef.current?.submitForm();
      },
    }),
    []
  );

  return (
    <Container
      ref={formRef}
      onSubmit={handleSubmit}
      className={`${className} row`}
    >
      <div className="col-12">
        <h2 className="h4 fw-semibold text-center mt-5 mb-4">
          5 - Modalidades
        </h2>
        <p className="mb-4">
          Cadastre ao menos uma modalidade para o seu evento para seguir para o
          próximo passo.
        </p>
      </div>
      <div className="col-12">
        <div className="d-flex justify-content-between align-items-center">
          <h3 className="h5 fw-normal roboto d-flex align-items-center">
            <MdOutlineWidgets color="#000" size={20} className="me-1" />
            Modalidades
          </h3>
          {/* <span>
            <IoMdInformationCircleOutline size={16} color="#6C6C6C" />
          </span> */}
        </div>
        <DragDropContext onDragEnd={handleChangeModalityOrder}>
          <Droppable droppableId="modalities">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {modalities.map((modality, index) => (
                  <Draggable
                    key={modality.input_id}
                    draggableId={modality.input_id.toString()}
                    index={index}
                  >
                    {(draggableProvided) => (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                      >
                        <Modality
                          modality={modality}
                          sold={sold}
                          onChangeModality={(value) =>
                            handleChangeModality(value, index)
                          }
                          onRemoveModality={() =>
                            handleRemoveModality(modality.input_id)
                          }
                          value={modality.name}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <button
          type="button"
          className="d-flex justify-content-center align-items-center rounded bd-add-new-mod bg-transparent p-2 w-100"
          onClick={handleAddModality}
        >
          <p className="h6 fw-normal btn-add-modality border-0 d-flex align-items-center mb-0">
            Adicionar nova modalidade
          </p>
          <div className="border-0 bg-transparent h-100">
            <HiPlus size={16} color="#ff6900" className="ms-2 " />{' '}
          </div>
        </button>
      </div>
      {modalitiesError && (
        <span className="small text-danger error">{modalitiesError}</span>
      )}
      <Loading active={loading} text={loadingText} />
    </Container>
  );
};

export default forwardRef(Modalities);
